import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { authApi } from '@/api/auth'
import { PasswordInput } from '@/components/common/PasswordInput'
import { PASSWORD_HINT, passwordSchema } from '@/lib/passwordPolicy'

// Правило пароля совпадает с backend password_meets_policy (ТЗ 4.1.3), см. lib/passwordPolicy.ts
const schema = z
  .object({
    password: passwordSchema,
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    message: 'Пароли не совпадают',
    path: ['confirm'],
  })

type FormData = z.infer<typeof schema>

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  // trim — на случай пробелов/переноса строки, если ссылку скопировали из письма вручную
  const token = (searchParams.get('token') ?? '').trim()
  const [serverError, setServerError] = useState('')
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-xl bg-white p-8 shadow-sm border border-gray-200 text-center max-w-sm">
          <p className="text-rose-600 font-medium">Ссылка недействительна</p>
          <p className="mt-2 text-sm text-gray-500">
            Убедитесь, что вы перешли по полной ссылке из письма.
          </p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-xl bg-white p-8 shadow-sm border border-gray-200 text-center max-w-sm space-y-4">
          <div className="text-emerald-500 text-5xl">✓</div>
          <h1 className="text-lg font-semibold text-gray-900">Пароль установлен</h1>
          <p className="text-sm text-gray-500">Теперь вы можете войти в систему.</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
          >
            Войти
          </button>
        </div>
      </div>
    )
  }

  async function onSubmit(data: FormData) {
    setServerError('')
    try {
      await authApi.setPassword(token, data.password)
      setSuccess(true)
    } catch (err: any) {
      const status = err?.response?.status
      const detail = err?.response?.data?.detail
      if (status === 422 && typeof detail === 'string') {
        // Пароль не прошёл политику или найден в списках утёкших паролей: причину показываем как есть
        setServerError(detail)
      } else if (status === 400 || status === 422) {
        setServerError('Ссылка недействительна или истекла. Запросите новое приглашение.')
      } else {
        setServerError('Ошибка сервера. Попробуйте позже.')
      }
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="text-2xl font-bold text-brand-600">СУП ГГП</div>
          <div className="mt-1 text-sm text-gray-500">Система управления проектами</div>
        </div>

        <div className="rounded-xl bg-white p-8 shadow-sm border border-gray-200">
          <h1 className="mb-2 text-lg font-semibold text-gray-900">Установка пароля</h1>
          <p className="mb-6 text-sm text-gray-500">
            Придумайте пароль для входа в систему.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Новый пароль
              </label>
              <PasswordInput
                autoComplete="new-password"
                {...register('password')}
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              {errors.password && (
                <p className="mt-1 text-xs text-rose-600">{errors.password.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">{PASSWORD_HINT}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Подтверждение пароля
              </label>
              <PasswordInput
                autoComplete="new-password"
                {...register('confirm')}
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              {errors.confirm && (
                <p className="mt-1 text-xs text-rose-600">{errors.confirm.message}</p>
              )}
            </div>

            {serverError && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
                {serverError}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? 'Сохранение...' : 'Установить пароль'}
            </button>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-gray-400">
          ГрандГеоПроект © 2026
        </p>
      </div>
    </div>
  )
}
